// src/components/Admin/AdministradoresAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Paper, Typography, Button, IconButton, Snackbar, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField, Tooltip, CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const schemaCreate = Yup.object({
    nombre: Yup.string().trim().min(2, "Mínimo 2 caracteres").required("Obligatorio"),
    email: Yup.string().trim().email("Email no válido").required("Obligatorio"),
    password: Yup.string().min(8, "Mínimo 8 caracteres").required("Obligatorio"),
});

const schemaEdit = Yup.object({
    nombre: Yup.string().trim().min(2, "Mínimo 2 caracteres").required("Obligatorio"),
    email: Yup.string().trim().email("Email no válido").required("Obligatorio"),
    // password opcional; si se rellena, validamos mínimo
    password: Yup.string().trim().nullable().transform(v => v === "" ? null : v)
        .min(8, "Mínimo 8 caracteres"),
});

export default function AdministradoresAdmin() {
    const { token } = useAuth();
    const API = process.env.REACT_APP_API_URL;

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const [openDlg, setOpenDlg] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [editRow, setEditRow] = useState(null);

    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
    const notify = (msg, sev = "success") => setSnack({ open: true, msg, sev });

    const fetchAdmins = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`${API}/admin/administradores`, { headers });
            const map = (a) => ({
                id: a.codigo,
                codigo: a.codigo,
                nombre: a.nombre ?? "",
                email: a.email ?? "",
                rol: a.rol ?? "ADMIN",
            });
            setRows(Array.isArray(data) ? data.map(map) : []);
        } catch (e) {
            console.error(e);
            notify("No se pudo cargar la lista de administradores", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAdmins(); }, [API, token]);

    const columns = [
        { field: "nombre", headerName: "Nombre", flex: 1, minWidth: 180 },
        { field: "email", headerName: "Email", flex: 1, minWidth: 220 },
        { field: "rol", headerName: "Rol", width: 120 },
        {
            field: "acciones",
            headerName: "Acciones",
            width: 120,
            sortable: false,
            renderCell: (params) => (
                <IconButton
                    aria-label="editar"
                    onClick={() => {
                        setEditRow(params.row);
                        setOpenEdit(true);
                    }}
                >
                    <EditIcon />
                </IconButton>
            ),
        },
    ];

    return (
        <Box p={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={1}>
                <Typography variant="h5">Administradores</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Recargar">
                        <span>
                            <IconButton onClick={fetchAdmins} disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDlg(true)}>
                        Nuevo admin
                    </Button>
                </Box>
            </Box>

            <Paper elevation={2} sx={{ height: 520, p: 1 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    disableRowSelectionOnClick
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                        pagination: { paginationModel: { page: 0, pageSize: 10 } },
                        sorting: { sortModel: [{ field: "codigo", sort: "asc" }] },
                    }}
                />
            </Paper>

            {/* Crear admin */}
            <Dialog open={openDlg} onClose={() => setOpenDlg(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Crear administrador</DialogTitle>
                <Formik
                    initialValues={{ nombre: "", email: "", password: "" }}
                    validationSchema={schemaCreate}
                    onSubmit={async (values, { setSubmitting, resetForm }) => {
                        try {
                            const payload = {
                                nombre: values.nombre.trim(),
                                email: values.email.trim(),
                                password: values.password,
                            };
                            await axios.post(`${API}/admin/administradores`, payload, { headers });
                            notify("Administrador creado correctamente");
                            resetForm();
                            setOpenDlg(false);
                            fetchAdmins();
                        } catch (e) {
                            console.error(e);
                            const msg = e?.response?.data?.message || e?.response?.data || "No se pudo crear el administrador";
                            notify(msg, "error");
                        } finally { setSubmitting(false); }
                    }}
                >
                    {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                        <Form>
                            <DialogContent dividers>
                                <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
                                    <TextField name="nombre" label="Nombre" value={values.nombre}
                                        onChange={handleChange} onBlur={handleBlur}
                                        error={touched.nombre && Boolean(errors.nombre)} helperText={touched.nombre && errors.nombre}
                                        fullWidth />
                                    <TextField name="email" label="Email" type="email" value={values.email}
                                        onChange={handleChange} onBlur={handleBlur}
                                        error={touched.email && Boolean(errors.email)} helperText={touched.email && errors.email}
                                        fullWidth />
                                    <TextField name="password" label="Password" type="password" value={values.password}
                                        onChange={handleChange} onBlur={handleBlur}
                                        error={touched.password && Boolean(errors.password)} helperText={touched.password && errors.password}
                                        fullWidth />
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenDlg(false)} disabled={isSubmitting}>Cancelar</Button>
                                <Button type="submit" variant="contained" disabled={isSubmitting}
                                    startIcon={isSubmitting ? <CircularProgress size={18} /> : null}>
                                    Crear
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            {/* Editar admin (incluye cambio de contraseña) */}
            <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Editar administrador</DialogTitle>
                {editRow && (
                    <Formik
                        enableReinitialize
                        initialValues={{ nombre: editRow.nombre || "", email: editRow.email || "", password: "" }}
                        validationSchema={schemaEdit}
                        onSubmit={async (values, { setSubmitting }) => {
                            try {
                                const payload = {
                                    nombre: values.nombre.trim(),
                                    email: values.email.trim(),
                                    // enviamos password solo si usuario ha escrito algo
                                    ...(values.password ? { password: values.password } : {}),
                                };
                                await axios.patch(`${API}/admin/administradores/${editRow.codigo}`, payload, { headers });
                                notify("Administrador actualizado");
                                setOpenEdit(false);
                                setEditRow(null);
                                fetchAdmins();
                            } catch (e) {
                                console.error(e);
                                const msg = e?.response?.data?.message || e?.response?.data || "No se pudo actualizar";
                                notify(msg, "error");
                            } finally { setSubmitting(false); }
                        }}
                    >
                        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                            <Form>
                                <DialogContent dividers>
                                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
                                        <TextField name="nombre" label="Nombre" value={values.nombre}
                                            onChange={handleChange} onBlur={handleBlur}
                                            error={touched.nombre && Boolean(errors.nombre)} helperText={touched.nombre && errors.nombre}
                                            fullWidth />
                                        <TextField name="email" label="Email" type="email" value={values.email}
                                            onChange={handleChange} onBlur={handleBlur}
                                            error={touched.email && Boolean(errors.email)} helperText={touched.email && errors.email}
                                            fullWidth />
                                        <TextField name="password" label="Nueva contraseña (opcional)" type="password" value={values.password}
                                            onChange={handleChange} onBlur={handleBlur}
                                            error={touched.password && Boolean(errors.password)} helperText={touched.password && errors.password}
                                            fullWidth />
                                    </Box>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setOpenEdit(false)} disabled={isSubmitting}>Cancelar</Button>
                                    <Button type="submit" variant="contained" disabled={isSubmitting}>
                                        Guardar
                                    </Button>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                )}
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                message={snack.msg}
            />
        </Box>
    );
}
